// Config resolver: merges @system defaults with @custom overrides.
// @system/info.js = template defaults (overwritten during template sync)
// @custom/info.js = product identity. Two writers exist and BOTH must work:
//   - scripts/apply-brand.js generates it from brand.json on every build and
//     exports `customInfo` (+ a legacy `info` = { ...baseInfo, ...customInfo });
//   - Orkosi provisioning writes it directly as `export const customInfo = {...}`.
//   The import below therefore reads the module namespace and accepts either
//   `customInfo` or `info`. Never import a single named binding here — a shape
//   mismatch used to spread `...undefined`, so every built customer app fell
//   back to the @system defaults ("ProductTemplate", slate, light) at runtime.
//
// Usage: import { info, text } from '@/config'
//   info — shallow merge: { ...systemInfo, ...customInfo } (@custom wins per key)
//   text — deep merge: @custom wins per leaf key, @system siblings are kept

import { info as systemInfo } from './@system/info.js'
import * as customMod from './@custom/info.js'
import { text as systemText } from './@system/text/index.js'
import { text as customText } from './@custom/text/index.js'

// The namespace is typed by whichever writer produced @custom/info.js last, so the
// legacy `info` binding may not exist in the type program — read it structurally.
const customNs = /** @type {{ customInfo?: Record<string, any>, info?: Record<string, any> }} */ (
  /** @type {unknown} */ (customMod)
)
const customInfo = customNs.customInfo ?? customNs.info ?? {}

/** @typedef {import('./@system/info.js').ProductInfo} ProductInfo */
/** @typedef {import('./@system/text/index.js').SiteText} SiteText */

/** @type {ProductInfo} */
export const info = { ...systemInfo, ...customInfo }

// Feature modules (brand.json `modules` → webpack __MODULES__). Re-exported so
// callers can `import { modules, isModuleEnabled } from '@/config'`.
export { modules, isModuleEnabled, filterByModules } from './@system/modules.js'

/**
 * Deep-merge `source` over `target`: nested plain objects merge per key, arrays and
 * primitives are replaced. Shape-agnostic on purpose — the caller pins the result type.
 * @param {Record<string, any>} target
 * @param {Record<string, any> | null | undefined} source
 * @returns {Record<string, any>}
 */
function deepMerge(target, source) {
  if (!source || typeof source !== 'object') return target
  /** @type {Record<string, any>} */
  const result = { ...target }
  for (const key of Object.keys(source)) {
    if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key]) &&
        target[key] && typeof target[key] === 'object' && !Array.isArray(target[key])) {
      result[key] = deepMerge(target[key], source[key])
    } else {
      result[key] = source[key]
    }
  }
  return result
}

// deepMerge is shape-agnostic (`T | Record<string, unknown>`); the @custom layer is
// checked against SiteTextOverrides (a DeepPartial<SiteText>) at its declaration, so
// the merge provably has the @system shape — the cast states that contract.
export const text = /** @type {SiteText} */ (deepMerge(systemText, customText))
